import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PhoneCall, HeartPulse, Sprout, Footprints, Sunrise, Volume2, VolumeX } from "lucide-react";

const STAGES = [
  { n: "01", word: "Reach Out", icon: PhoneCall, meaning: "It begins with a single, courageous call.", body: "The hardest step is the first one. When you reach out, you're met with warmth and zero judgement — just people ready to help you begin.", image: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=compress&w=1900&q=80" },
  { n: "02", word: "Stabilize", icon: HeartPulse, meaning: "Safe, on-call medical care.", body: "In our residential home you're held safe, with 24/7 supervision and on-call medical support, so your body can find steady ground again.", image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=compress&w=1900&q=80" },
  { n: "03", word: "Heal", icon: Sprout, meaning: "Therapy for mind, body and spirit.", body: "Through CBT, DBT, mindfulness and individual and group therapy, we care for the whole person — gently untangling what addiction touched.", image: "https://images.unsplash.com/photo-1518495973542-4542c06a5843?auto=compress&w=1900&q=80" },
  { n: "04", word: "Grow", icon: Footprints, meaning: "Real skills, practised in real life.", body: "Recovery isn't learned in a bubble. You apply your new skills in the real world — building confidence that lasts long after treatment.", image: "https://images.unsplash.com/photo-1501183638710-841dd1904471?auto=compress&w=1900&q=80" },
  { n: "05", word: "Begin Again", icon: Sunrise, meaning: "A whole new life, supported for good.", body: "Healing is just the start. With lifetime aftercare, family and the same trusted team beside you, your new beginning becomes a new life.", image: "https://images.unsplash.com/photo-1504198453319-5ce911bafcde?auto=compress&w=1900&q=80" },
];

const Stage = ({ i, total, progress }) => {
  const seg = 1 / total;
  const center = i * seg + seg / 2;
  const range = [center - seg * 0.62, center, center + seg * 0.62];
  const opacity = useTransform(progress, range, [0, 1, 0]);
  const y = useTransform(progress, range, [70, 0, -70]);
  const imgScale = useTransform(progress, [center - seg * 0.62, center + seg * 0.62], [1.15, 1]);
  const s = STAGES[i];
  const Icon = s.icon;

  return (
    <motion.div style={{ opacity }} className="absolute inset-0">
      {/* crossfading cinematic background */}
      <motion.div style={{ scale: imgScale }} className="absolute inset-0">
        <img src={s.image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forestdark/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-forestdark via-forestdark/40 to-forestdark/70" />
      </motion.div>

      {/* content */}
      <motion.div style={{ y }} className="absolute inset-0 flex items-center">
        <div className="mx-auto max-w-[1400px] w-full px-5 md:px-10 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-2 hidden lg:block">
            <span className="font-serif text-[10rem] leading-none text-sand/10 select-none">{s.n}</span>
          </div>
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4 mb-6">
              <span className="grid place-items-center w-14 h-14 rounded-full bg-terracotta/25 text-terracotta shrink-0">
                <Icon className="w-6 h-6" />
              </span>
              <span className="text-sm uppercase tracking-[0.3em] text-sage">Step {s.n}</span>
            </div>
            <h3 className="font-serif text-6xl md:text-8xl text-sand tracking-tight leading-[0.95]">{s.word}</h3>
            <p className="mt-5 font-serif italic text-2xl md:text-3xl text-terracotta">{s.meaning}</p>
            <p className="mt-6 max-w-xl text-lg text-sand/75 leading-relaxed">{s.body}</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const RecoveryJourney = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const lineWidth = useTransform(scrollYProgress, [0, 1], ["8%", "100%"]);

  const audioRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);

  const toggleSound = () => {
    if (!soundOn) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const gain = ctx.createGain();
        gain.gain.value = 0;
        gain.connect(ctx.destination);
        const filter = ctx.createBiquadFilter();
        filter.type = "lowpass";
        filter.frequency.value = 620;
        filter.connect(gain);
        const oscs = [110, 164.81, 220].map((f) => {
          const o = ctx.createOscillator();
          o.type = "sine";
          o.frequency.value = f;
          o.connect(filter);
          o.start();
          return o;
        });
        gain.gain.linearRampToValueAtTime(0.05, ctx.currentTime + 2);
        audioRef.current = { ctx, gain, oscs };
        setSoundOn(true);
      } catch (e) {
        /* audio unavailable */
      }
    } else {
      const a = audioRef.current;
      if (a) {
        try {
          a.gain.gain.linearRampToValueAtTime(0, a.ctx.currentTime + 0.5);
          setTimeout(() => { a.oscs.forEach((o) => { try { o.stop(); } catch (e) { /* noop */ } }); a.ctx.close(); }, 600);
        } catch (e) { /* noop */ }
      }
      audioRef.current = null;
      setSoundOn(false);
    }
  };

  useEffect(() => {
    STAGES.forEach((s) => { const img = new Image(); img.src = s.image; });
  }, []);

  useEffect(() => () => {
    const a = audioRef.current;
    if (a) { try { a.oscs.forEach((o) => o.stop()); a.ctx.close(); } catch (e) { /* noop */ } }
  }, []);

  return (
    <section
      ref={ref}
      data-testid="recovery-journey"
      className="relative bg-forestdark"
      style={{ height: `${STAGES.length * 100}vh` }}
    >
      <div className="sticky top-0 h-screen overflow-hidden grain">
        {/* Stages (each carries its own cinematic background) */}
        {STAGES.map((_, i) => (
          <Stage key={i} i={i} total={STAGES.length} progress={scrollYProgress} />
        ))}

        {/* Heading */}
        <div className="absolute top-24 md:top-28 left-1/2 -translate-x-1/2 text-center z-10 px-5">
          <p className="text-sm uppercase tracking-[0.3em] text-sage mb-2">The Recovery Journey</p>
          <h2 className="font-serif text-3xl md:text-4xl text-sand/90">Every step, toward the light</h2>
        </div>

        {/* Ambient sound toggle */}
        <button
          onClick={toggleSound}
          data-testid="journey-sound-toggle"
          aria-label={soundOn ? "Mute ambient sound" : "Play ambient sound"}
          className="absolute top-24 md:top-28 right-5 md:right-10 z-20 flex items-center gap-2 rounded-full border border-sand/25 bg-forestdark/50 backdrop-blur px-4 py-2 text-sand/80 hover:text-sand hover:border-sage transition-colors text-sm"
        >
          {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          <span className="hidden sm:inline">{soundOn ? "Sound on" : "Ambient sound"}</span>
        </button>

        {/* Progress rail */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-[86%] max-w-3xl z-10">
          <div className="h-[3px] rounded-full bg-sand/15 overflow-hidden">
            <motion.div style={{ width: lineWidth }} className="h-full bg-terracotta rounded-full" />
          </div>
          <div className="mt-3 flex justify-between text-[10px] md:text-xs uppercase tracking-[0.15em] text-sand/50">
            {STAGES.map((s) => (
              <span key={s.n}>{s.word}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecoveryJourney;
